<script setup>
import {computed, onMounted, ref} from 'vue';
import {usePortalContentAndDesignStore} from "../../../../../../../stores/partners/portalContentAndDesignStore";
import InviteFriendsByEmail from "../../../../../InviteFriendsByEmail.vue";
import EditableTextField from "../../../../../portal-editable-fields/EditableTextField.vue";
import RequestPayout from "../../../../../payouts/RequestPayout.vue";
import {isCustomColor, getByKey, normalizeObjectByLowercaseKeys} from "../../../../../../../helpers/common.js";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

onMounted(() => {
    portalContentAndDesignStore.initializePortalPageContent('dashboard');
    portalContentAndDesignStore.initializePortalPageContent('payouts');
});

// Dashboard data
const dashboardSettings = computed(() => portalContentAndDesignStore.dashboardPageSettings || {});
const stats = computed(() => dashboardSettings.value.dashboard_stats || {});
const referralLink = computed(() => dashboardSettings.value.referral_link || '');
const rewardBalanceStats = computed(() => dashboardSettings.value.reward_balance_stats || []);
const unlockedRewards = computed(() => dashboardSettings.value.unlocked_rewards || []);
const referralsActivities = computed(() => dashboardSettings.value.referrals_activities || []);

// Program settings
const programSettings = computed(() => portalContentAndDesignStore.portalLayoutSettings || {});
const translationSettings = computed(() => portalContentAndDesignStore.translationSettings || {});

// Color and layout settings
const primaryColor = computed(() => programSettings.value.portal_layout_brand_primary_color || 'teal-600');

const contentLayoutCorners = computed(() => programSettings.value.referral_portal_content_layout_corners || 'rounded-xl');
const contentLayoutStyle = computed(() => programSettings.value.referral_portal_content_layout_style || 'border');

// Color classes with custom color support
const contentTextColor = computed(() => {
    const color = programSettings.value.referral_portal_content_text_color;
    return isCustomColor(color) ? 'content-custom-text' : color || 'gray-800';
});

const contentTextColorSecondary = computed(() => {
    const color = programSettings.value.referral_portal_content_text_color_secondary;
    return isCustomColor(color) ? 'content-custom-text-secondary' : color || 'gray-800';
});

const contentBorderColor = computed(() => {
    const color = programSettings.value.referral_portal_content_border_color;
    return isCustomColor(color) ? 'content-custom-color' : color || 'gray-200';
});

const contentLinkColor = computed(() => {
    const color = programSettings.value.referral_portal_content_link_color;
    return isCustomColor(color) ? 'content-custom-link' : color || 'teal-600';
});

const contentInputStyle = computed(() => programSettings.value.referral_portal_content_input_style || 'white');

// Card classes based on layout settings
const cardClasses = computed(() => {
    let classes = 'p-6 ';
    
    // Corners
    if (contentLayoutCorners.value === 'none') {
        classes += 'rounded-sm ';
    } else {
        classes += contentLayoutCorners.value + ' ';
    }
    
    // Style
    classes += contentLayoutStyle.value + ' ';
    
    if (contentLayoutStyle.value === 'border') {
        const borderColor = programSettings.value.referral_portal_content_border_color || 'gray-200';
        if (isCustomColor(borderColor)) {
            classes += `border-content-custom-color `;
        } else {
            classes += `border-${borderColor} `;
        }
    }
    
    if (contentLayoutStyle.value === 'shadow') {
        classes += 'border ';
    }
    
    return classes;
});

// Section container classes for tables and lists
const sectionContainerClasses = computed(() => {
    let classes = '';
    
    // Corners
    if (contentLayoutCorners.value === 'none') {
        classes += 'rounded-sm ';
    } else {
        classes += contentLayoutCorners.value + ' ';
    }
    
    // Style
    classes += contentLayoutStyle.value + ' ';
    
    if (contentLayoutStyle.value === 'border') {
        const borderColor = programSettings.value.referral_portal_content_border_color || 'gray-200';
        if (isCustomColor(borderColor)) {
            classes += `border-content-custom-color `;
        } else {
            classes += `border-${borderColor} `;
        }
    }
    
    if (contentLayoutStyle.value === 'shadow') {
        classes += 'border ';
    }
    
    const borderColor = programSettings.value.referral_portal_content_border_color || 'gray-200';
    if (isCustomColor(borderColor)) {
        classes += `divide-y divide-content-custom-color`;
    } else {
        classes += `divide-y divide-${borderColor}`;
    }
    
    return classes;
});

// Show/hide sections based on settings (from dashboardPageSettings, not programSettings)
// Note: show_stats might be missing from API response, so we default to true
const showStats = computed(() => dashboardSettings.value.show_stats !== false);
const showSharingViaLink = computed(() => dashboardSettings.value.show_sharing_via_link === true);
const showSharingViaEmail = computed(() => dashboardSettings.value.show_sharing_via_email === true);
const showRewardBalance = computed(() => dashboardSettings.value.show_reward_balance === true);
const showUnlockedRewards = computed(() => dashboardSettings.value.show_unlocked_rewards === true);
const showRewardsHistory = computed(() => dashboardSettings.value.show_rewards_history === true);
const showPayouts = computed(() => dashboardSettings.value.show_payouts === true);

// Payouts data (loaded lazily from the payouts page settings endpoint)
const payoutsSettings = computed(() => portalContentAndDesignStore.payoutsPageSettings || {});
const payoutsLoaded = computed(() => Object.keys(payoutsSettings.value).length > 0);
const payoutOptions = computed(() => payoutsSettings.value.payout_options || []);
const payoutRequestsDisabled = computed(() => payoutsSettings.value.payout_requests_disabled === true);
const payoutHistoryRows = computed(() => payoutsSettings.value.payouts?.data || []);
const normalizedPreferredSettings = computed(() => normalizeObjectByLowercaseKeys(payoutsSettings.value.preferred_payout_settings));
const normalizedPayoutGateways = computed(() => normalizeObjectByLowercaseKeys(payoutsSettings.value.payout_gateway_options));

const cancelPayoutLoadingId = ref(null);
const cancelablePayoutStatuses = computed(() => payoutsSettings.value.cancel_payout_allowed_statuses || []);
const canCancelPayout = (payout) => cancelablePayoutStatuses.value.includes(payout.status);

const cancelPayout = (payout) => {
    if (cancelPayoutLoadingId.value || !canCancelPayout(payout)) {
        return;
    }
    cancelPayoutLoadingId.value = payout.id;
    axios.post(portalContentAndDesignStore.getPayoutCancelRoute, {payout: payout.id})
        .then(response => {
            if (parseInt(response.data.status ?? -1) === 1) {
                portalContentAndDesignStore.payoutsPageSettings.payouts.data =
                    payoutHistoryRows.value.filter(row => row.id !== payout.id);
                portalContentAndDesignStore.initializePortalPageContent('payouts');
            }
        })
        .finally(() => cancelPayoutLoadingId.value = null);
};

const payoutStatusLabel = (status) => {
    const labels = {
        requested: 'Requested',
        approved: 'Approved',
        finished: 'Paid',
        rejected: 'Rejected',
        resubmit_requested: 'Resubmit requested',
        resubmitted: 'Resubmitted',
        approved_auto_scheduled: 'Scheduled',
        mass_approved_scheduled: 'Scheduled',
        intermediary_approved_scheduled: 'Scheduled',
        failed_auto_scheduled: 'Failed',
    };
    return labels[status] || (status ? status.replace(/_/g, ' ') : '');
};

// Copy functionality state
const isCopied = ref(false);

// Helper methods
const copyToClipboard = (text) => {
    // Check if the Clipboard API is available
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
            console.log('Copied to clipboard:', text);
            showCopiedFeedback();
        }).catch(err => {
            console.error('Failed to copy text: ', err);
        });
    } else {
        // Fallback for older browsers or non-HTTPS environments
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
            const successful = document.execCommand('copy');
            if (successful) {
                console.log('Copied to clipboard (fallback):', text);
                showCopiedFeedback();
            } else {
                console.error('Failed to copy text using fallback method');
            }
        } catch (err) {
            console.error('Failed to copy text: ', err);
        } finally {
            document.body.removeChild(textArea);
        }
    }
};

const showCopiedFeedback = () => {
    isCopied.value = true;
    setTimeout(() => {
        isCopied.value = false;
    }, 2000); // Reset after 2 seconds
};

const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
};

// Header data
const userName = computed(() => {
    const user = portalContentAndDesignStore.authUser;
    return user ? `${user.name || ''} ${user.last_name || ''}`.trim() : '';
});

const userEmail = computed(() => {
    return portalContentAndDesignStore.authUser?.email || '';
});

const logoutUrl = computed(() => {
    return portalContentAndDesignStore.getLogoutRoute || '/logout';
});

// Program branding
const programTitle = computed(() => {
    return dashboardSettings.value.refer_a_friend_portal_page_title || 
           portalContentAndDesignStore.program?.name || 
           'Referral Program';
});

const programDescription = computed(() => {
    return dashboardSettings.value.refer_a_friend_portal_page_description || '';
});

// Program branding settings
const dashboardBrandingType = computed(() => {
    return dashboardSettings.value.refer_a_friend_portal_dashboard_branding || 'symbol';
});

const showProgramSymbol = computed(() => {
    return dashboardBrandingType.value === 'symbol' && 
           (portalContentAndDesignStore.srcRoutes?.portal_layout_file_symbol || portalContentAndDesignStore.srcRoutes?.portal_layout_file_logo);
});

const showProgramLogo = computed(() => {
    return dashboardBrandingType.value === 'logo' && 
           portalContentAndDesignStore.srcRoutes?.portal_layout_file_logo;
});

const programSymbolStyle = computed(() => {
    const symbolUrl = portalContentAndDesignStore.srcRoutes?.portal_layout_file_symbol;
    if (symbolUrl) {
        return {
            backgroundImage: `url('${symbolUrl}')`
        };
    }
    return {};
});

const programLogoSrc = computed(() => {
    return portalContentAndDesignStore.srcRoutes?.portal_layout_file_logo || '';
});

// Container styling (same as program page)
const getContentContainerClasses = computed(() => {
    const layoutStyle = programSettings.value.referral_portal_content_layout_style || 'border';
    const corners = programSettings.value.referral_portal_content_layout_corners || 'rounded-xl';
    const borderColor = programSettings.value.referral_portal_content_border_color || 'gray-200';
    const backgroundColor = programSettings.value.referral_portal_content_background_color || 'white';
    
    let classes = `bg-${isCustomColor(backgroundColor) ? 'content-custom-bg' : backgroundColor} ${layoutStyle}`;
    
    if (layoutStyle === 'border') {
        if (isCustomColor(borderColor)) {
            classes += ` border border-content-custom-color`;
        } else {
            classes += ` border border-${borderColor}`;
        }
    } else if (layoutStyle === 'shadow') {
        classes += ' border shadow-lg';
    }
    
    classes += ` ${corners === 'none' ? 'rounded-sm' : corners}`;
    
    return classes;
});

const getHeaderClasses = computed(() => {
    const layoutStyle = programSettings.value.referral_portal_content_layout_style || 'border';
    const borderColor = programSettings.value.referral_portal_content_border_color || 'gray-200';
    
    let classes = 'p-4 md:p-10 lg:p-14 py-10 lg:py-14';
    
    if (layoutStyle === 'border') {
        if (isCustomColor(borderColor)) {
            classes += ` border-b border-content-custom-color`;
        } else {
            classes += ` border-b border-${borderColor}`;
        }
    }
    
    return classes;
});

const formatCurrencyAmount = (amount, units) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: units || 'USD'
    }).format(amount);
};

// Invite friends translations - using correct translation keys from store
const inviteFriendsTranslations = computed(() => ({
    inviteFriendsByEmail: translationSettings.value.portal_translation_raf_portal_invite_friends_by_email || 'Invite friends by email',
    inviteFriendsByEmailDescription: translationSettings.value.portal_translation_raf_portal_invite_friends_by_email_desc || 'Send email invitations to your friends',
    inviteFriendsByEmailNote: translationSettings.value.portal_translation_raf_portal_invite_friends_by_email_note || 'Note: Your friends will receive an email invitation',
    sending: translationSettings.value.portal_translation_raf_portal_sending || 'Sending...',
    sendInvite: translationSettings.value.portal_translation_raf_portal_send_invite || 'Send invite',
}));
</script>

<template>
    <div v-if="portalContentAndDesignStore.portalInitialized" :class="getContentContainerClasses">
        
        <!-- Dashboard Header -->
        <div :class="getHeaderClasses">
            
            <div class="flex items-center justify-between gap-10">
                <!-- Program Info -->
                <div class="flex items-center gap-4">
                    <!-- Program Logo/Symbol -->
                    <div v-if="showProgramSymbol" 
                         class="relative h-20 w-20 lg:h-32 lg:w-32 flex-shrink-0 bg-cover bg-center"
                         :class="[
                             contentLayoutCorners === 'none' ? 'rounded-sm' : contentLayoutCorners,
                             `bg-${isCustomColor(primaryColor) ? 'custom-primary' : primaryColor}`
                         ]"
                         :style="programSymbolStyle">
                        <div class="absolute h-full w-full border border-black border-opacity-10"
                             :class="contentLayoutCorners === 'none' ? 'rounded-sm' : contentLayoutCorners">
                        </div>
                    </div>
                    
                    <div v-else-if="showProgramLogo">
                        <img :src="programLogoSrc" class="max-w-64 h-auto" />
                    </div>
                    
                    <!-- Program Title & Description -->
                    <div>
                        <h1 class="mb-2 text-xl md:text-2xl lg:text-3xl font-semibold" 
                            :class="`text-${contentTextColor}`">
                            {{ programTitle }}
                        </h1>
                        <div v-if="programDescription" 
                             class="pt-program-content text-md lg:text-lg space-y-2 text-opacity-75"
                             :class="`text-${contentTextColor}`"
                             v-html="programDescription">
                        </div>
                    </div>
                </div>
                
                <!-- User Info -->
                <!-- Mobile User Menu -->
                <div class="cursor-pointer flex lg:hidden">
                    <!-- This would need a dropdown component, for now just show basic info -->
                    <div class="rounded-full flex items-center justify-center font-medium text-gray-500 bg-gray-200 w-10 h-10">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" class="fill-current">
                            <path d="M4 22C4 17.5817 7.58172 14 12 14C16.4183 14 20 17.5817 20 22H18C18 18.6863 15.3137 16 12 16C8.68629 16 6 18.6863 6 22H4ZM12 13C8.685 13 6 10.315 6 7C6 3.685 8.685 1 12 1C15.315 1 18 3.685 18 7C18 10.315 15.315 13 12 13ZM12 11C14.21 11 16 9.21 16 7C16 4.79 14.21 3 12 3C9.79 3 8 4.79 8 7C8 9.21 9.79 11 12 11Z"></path>
                        </svg>
                    </div>
                </div>
                
                <!-- Desktop User Info -->
                <div class="hidden lg:flex items-center gap-4">
                    <div class="rounded-full flex items-center justify-center font-medium text-gray-500 bg-gray-200 w-10 h-10">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" class="fill-current">
                            <path d="M4 22C4 17.5817 7.58172 14 12 14C16.4183 14 20 17.5817 20 22H18C18 18.6863 15.3137 16 12 16C8.68629 16 6 18.6863 6 22H4ZM12 13C8.685 13 6 10.315 6 7C6 3.685 8.685 1 12 1C15.315 1 18 3.685 18 7C18 10.315 15.315 13 12 13ZM12 11C14.21 11 16 9.21 16 7C16 4.79 14.21 3 12 3C9.79 3 8 4.79 8 7C8 9.21 9.79 11 12 11Z"></path>
                        </svg>
                    </div>
                    
                    <div class="flex flex-col border-r pr-4" :class="isCustomColor(programSettings.referral_portal_content_border_color) ? 'border-content-custom-color' : `border-${contentBorderColor}`">
                        <span class="text-sm tracking-wide" :class="`text-${contentTextColorSecondary}`">
                            {{ userName }}
                        </span>
                        <span class="text-xs tracking-wide opacity-75" :class="`text-${contentTextColorSecondary}`">
                            {{ userEmail }}
                        </span>
                    </div>
                    
                    <a :href="logoutUrl" 
                       class="hover:opacity-75" 
                       :class="`text-${contentTextColorSecondary}`"
                       title="Log out">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" class="fill-current">
                            <path d="M5 22C4.44772 22 4 21.5523 4 21V3C4 2.44772 4.44772 2 5 2H19C19.5523 2 20 2.44772 20 3V6H18V4H6V20H18V18H20V21C20 21.5523 19.5523 22 19 22H5ZM18 16V13H11V11H18V8L23 12L18 16Z"></path>
                        </svg>
                    </a>
                </div>
            </div>
        </div>

        <!-- Dashboard Content -->
        <div class="p-4 md:p-10 lg:p-14 space-y-6 lg:space-y-10">
            <!-- Your Stats Section -->
        <section v-if="showStats" class="space-y-4">
            <div class="space-y-2">
                <EditableTextField
                    text-type="h2"
                    class="text-xl font-semibold"
                    :class="`text-${contentTextColor}`"
                    :model-value="translationSettings.portal_translation_raf_portal_your_stats || 'Your stats'"
                    field-key="your_stats"
                />
            </div>

            <div class="grid lg:grid-cols-3 gap-4">
                <!-- Friends signed up -->
                <div :class="cardClasses">
                    <div class="text-sm opacity-75 mb-4" :class="`text-${contentTextColorSecondary}`">
                        <EditableTextField
                            text-type="p"
                            class="text-sm opacity-75"
                            :class="`text-${contentTextColorSecondary}`"
                            :model-value="translationSettings.portal_translation_raf_portal_friends_sign_up || 'Friends signed up'"
                            field-key="friends_sign_up"
                        />
                    </div>
                    <h3 class="text-2xl xl:text-3xl font-semibold leading-tight" :class="`text-${contentTextColorSecondary}`">
                        {{ stats.partner_total_signups || 0 }}
                    </h3>
                </div>

                <!-- Friends upgraded -->
                <div :class="cardClasses">
                    <div class="text-sm opacity-75 mb-4" :class="`text-${contentTextColorSecondary}`">
                        <EditableTextField
                            text-type="p"
                            class="text-sm opacity-75"
                            :class="`text-${contentTextColorSecondary}`"
                            :model-value="translationSettings.portal_translation_raf_portal_friends_upgraded || 'Friends upgraded'"
                            field-key="friends_upgraded"
                        />
                    </div>
                    <h3 class="text-2xl xl:text-3xl font-semibold leading-tight" :class="`text-${contentTextColorSecondary}`">
                        {{ stats.partner_total_friends_upgraded || 0 }}
                    </h3>
                </div>

                <!-- Rewards unlocked -->
                <div :class="cardClasses">
                    <div class="text-sm opacity-75 mb-4" :class="`text-${contentTextColorSecondary}`">
                        <EditableTextField
                            text-type="p"
                            class="text-sm opacity-75"
                            :class="`text-${contentTextColorSecondary}`"
                            :model-value="translationSettings.portal_translation_raf_portal_rewards_unlocked || 'Rewards unlocked'"
                            field-key="rewards_unlocked"
                        />
                    </div>
                    <h3 class="text-2xl xl:text-3xl font-semibold leading-tight" :class="`text-${contentTextColorSecondary}`">
                        {{ stats.partner_total_unlocked_rewards || 0 }}
                    </h3>
                </div>
            </div>
        </section>

        <!-- Share Your Link Section -->
        <section v-if="showSharingViaLink" class="space-y-4">
            <div class="space-y-2">
                <EditableTextField
                    text-type="h2"
                    class="text-xl font-semibold"
                    :class="`text-${contentTextColor}`"
                    :model-value="translationSettings.portal_translation_raf_portal_share_your_link || 'Share your link'"
                    field-key="share_your_link"
                />
                <EditableTextField
                    text-type="p"
                    class="text-sm opacity-75"
                    :class="`text-${contentTextColor}`"
                    :model-value="translationSettings.portal_translation_raf_portal_share_your_link_description || 'Share your unique referral link by copying and sending it or sharing it on your social media.'"
                    field-key="share_your_link_description"
                />
            </div>

            <div class="grid grid-cols-5 gap-4 w-full items-center justify-between">
                <!-- Referral Link Input -->
                <div class="lg:col-span-3 col-span-5">
                    <input
                        :class="`w-full h-11 min-w-0 flex-auto border-0 px-3.5 py-2.5 ring-1 ring-inset ${contentInputStyle === 'white' ? 'ring-gray-300' : 'ring-' + contentTextColor} focus:ring-2 focus:ring-inset focus:ring-${primaryColor} ${contentLayoutCorners === 'none' ? 'rounded-sm' : contentLayoutCorners} sm:text-sm sm:leading-6 ${contentInputStyle === 'white' ? 'bg-white text-gray-900' : 'bg-transparent text-' + contentTextColor}`"
                        :value="referralLink"
                        disabled
                    />
                </div>

                <!-- Copy Button -->
                <div class="lg:col-span-1 col-span-5">
                    <button
                        @click="copyToClipboard(referralLink)"
                        :class="[
                            'w-full h-11 flex items-center justify-center gap-1.5 px-3.5 py-2.5 text-sm font-semibold text-white hover:bg-opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-colors',
                            contentLayoutCorners === 'none' ? 'rounded-sm' : contentLayoutCorners,
                            isCustomColor(primaryColor) ? 'bg-custom-primary' : `bg-${primaryColor}`
                        ]"
                        :style="isCustomColor(primaryColor) ? { backgroundColor: primaryColor } : {}"
                    >
                        <svg v-if="!isCopied" class="h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="M6.9998 6V3C6.9998 2.44772 7.44752 2 7.9998 2H19.9998C20.5521 2 20.9998 2.44772 20.9998 3V17C20.9998 17.5523 20.5521 18 19.9998 18H16.9998V20.9991C16.9998 21.5519 16.5499 22 15.993 22H4.00666C3.45059 22 3 21.5554 3 20.9991L3.0026 7.00087C3.0027 6.44811 3.45264 6 4.00942 6H6.9998ZM8.9998 6H16.9998V16H18.9998V4H8.9998V6Z" fill="currentColor"></path>
                        </svg>
                        <svg v-else class="h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path d="M9.9997 15.1709L19.1921 5.97852L20.6063 7.39273L9.9997 17.9993L3.63574 11.6354L5.04996 10.2212L9.9997 15.1709Z" fill="currentColor"></path>
                        </svg>
                        <EditableTextField
                            text-type="span"
                            :model-value="isCopied ? (translationSettings.portal_translation_general_copied || 'Copied!') : (translationSettings.portal_translation_general_copy_url || 'Copy link')"
                            :field-key="isCopied ? 'copied' : 'copy_url'"
                        />
                    </button>
                </div>

                <!-- Social Share Buttons -->
                <div class="lg:col-span-1 col-span-5">
                    <div class="isolate inline-flex rounded-md w-full">
                        <!-- Facebook -->
                        <a
                            :href="`https://www.facebook.com/sharer/sharer.php?u=${referralLink}`"
                            target="_blank"
                            :class="[
                                'w-1/3 h-11 relative inline-flex items-center justify-center px-3.5 py-2.5 text-sm font-semibold text-white hover:bg-opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 rounded-r-none',
                                contentLayoutCorners === 'none' ? 'rounded-sm' : contentLayoutCorners,
                                isCustomColor(primaryColor) ? 'bg-custom-primary' : `bg-${primaryColor}`
                            ]"
                            :style="isCustomColor(primaryColor) ? { backgroundColor: primaryColor } : {}"
                        >
                            <svg class="h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                <path d="M14 13.5H16.5L17.5 9.5H14V7.5C14 6.47062 14 5.5 16 5.5H17.5V2.1401C17.1743 2.09685 15.943 2 14.6429 2C11.9284 2 10 3.65686 10 6.69971V9.5H7V13.5H10V22H14V13.5Z" fill="currentColor"></path>
                            </svg>
                        </a>

                        <!-- LinkedIn -->
                        <a
                            :href="`https://www.linkedin.com/sharing/share-offsite/?url=${referralLink}`"
                            target="_blank"
                            :class="[
                                'w-1/3 h-11 relative -ml-px inline-flex items-center justify-center px-3.5 py-2.5 text-sm font-semibold text-white hover:bg-opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
                                isCustomColor(primaryColor) ? 'bg-custom-primary' : `bg-${primaryColor}`
                            ]"
                            :style="isCustomColor(primaryColor) ? { backgroundColor: primaryColor } : {}"
                        >
                            <svg class="h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                <path d="M6.94048 4.99993C6.94011 5.81424 6.44608 6.54702 5.69134 6.85273C4.9366 7.15845 4.07187 6.97605 3.5049 6.39155C2.93793 5.80704 2.78195 4.93715 3.1105 4.19207C3.43906 3.44699 4.18654 2.9755 5.00048 2.99993C6.08155 3.03238 6.94097 3.91837 6.94048 4.99993ZM7.00048 8.47993H3.00048V20.9999H7.00048V8.47993ZM13.3205 8.47993H9.34048V20.9999H13.2805V14.4299C13.2805 10.7699 18.0505 10.4299 18.0505 14.4299V20.9999H22.0005V13.0699C22.0005 6.89993 14.9405 7.12993 13.2805 10.1599L13.3205 8.47993Z" fill="currentColor"></path>
                            </svg>
                        </a>

                        <!-- Twitter/X -->
                        <a
                            :href="`https://twitter.com/intent/tweet?url=${referralLink}`"
                            target="_blank"
                            :class="[
                                'w-1/3 h-11 relative -ml-px inline-flex items-center justify-center px-3.5 py-2.5 text-sm font-semibold text-white hover:bg-opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 rounded-l-none',
                                contentLayoutCorners === 'none' ? 'rounded-sm' : contentLayoutCorners,
                                isCustomColor(primaryColor) ? 'bg-custom-primary' : `bg-${primaryColor}`
                            ]"
                            :style="isCustomColor(primaryColor) ? { backgroundColor: primaryColor } : {}"
                        >
                            <svg class="h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                <path d="M18.2048 2.25H21.5128L14.2858 10.51L22.7878 21.75H16.1308L10.9168 14.933L4.95084 21.75H1.64084L9.37084 12.915L1.21484 2.25H8.04084L12.7538 8.481L18.2048 2.25ZM17.0438 19.77H18.8768L7.04484 4.126H5.07784L17.0438 19.77Z" fill="currentColor"></path>
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </section>

        <!-- Invite Friends by Email Section -->
        <section v-if="showSharingViaEmail" class="space-y-4">
            <invite-friends-by-email
                :primary-color="isCustomColor(primaryColor) ? 'custom-primary' : primaryColor"
                :content-text-color="contentTextColor"
                :content-text-color-is-custom="isCustomColor(programSettings.referral_portal_content_text_color) ? 1 : 0"
                :referral-portal-content-layout-corners="contentLayoutCorners"
                :translations="inviteFriendsTranslations"
            />
        </section>

        <!-- Reward Balance Section -->
        <section v-if="showRewardBalance && rewardBalanceStats.length > 0" class="space-y-4">
            <div class="flex items-center justify-between gap-4 flex-wrap">
                <EditableTextField
                    text-type="h2"
                    class="text-xl font-semibold"
                    :class="`text-${contentTextColor}`"
                    :model-value="translationSettings.portal_translation_raf_reward_balance || 'Reward balance'"
                    field-key="reward_balance"
                />
                <div
                    v-if="showPayouts && payoutsLoaded && !payoutRequestsDisabled && payoutOptions.length > 0"
                    class="flex flex-wrap gap-3"
                >
                    <request-payout
                        v-for="option in payoutOptions"
                        :key="`balance-${option.reward_amount_units}`"
                        :amount-unit="option.reward_amount_units || ''"
                        :amount-with-units-formatted="option.reward_amount_total_formatted"
                        :preferred-settings="getByKey(normalizedPreferredSettings, option.reward_amount_units)"
                        :available-methods="getByKey(normalizedPayoutGateways, option.reward_amount_units)"
                        :payout-invoice-requirements="payoutsSettings.payout_invoice_requirements"
                        :bill-to-details="payoutsSettings.bill_to_details"
                    />
                </div>
            </div>

            <div :class="sectionContainerClasses">
                <div
                    v-for="stat in rewardBalanceStats"
                    :key="stat.units"
                    class="grid grid-cols-3 lg:grid-cols-6 gap-4 p-4 flex items-center w-full"
                >
                    <div class="col-span-2">
                        <div class="text-sm tracking-wide opacity-75 mb-0.5" :class="`text-${contentTextColorSecondary}`">
                            {{ translationSettings.portal_translation_raf_balance_unit || 'Balance unit' }}
                        </div>
                        <div class="uppercase text-sm font-semibold" :class="`text-${contentTextColorSecondary}`">
                            {{ stat.units }}
                        </div>
                    </div>
                    <div class="col-span-2">
                        <span :class="`inline-flex items-center px-1.5 py-0.5 text-xs align-middle font-medium ${contentLayoutCorners === 'none' ? 'rounded-sm' : contentLayoutCorners} text-white bg-${isCustomColor(primaryColor) ? 'custom-primary' : primaryColor}`">
                            <span>
                                {{ stat.is_currency ? (translationSettings.portal_translation_raf_balance_currency || 'Currency') : (stat.reward_type === 'shopify_store_credit' ? 'Store credit' : (translationSettings.portal_translation_raf_balance_non_currency || 'Non-currency')) }}
                            </span>
                        </span>
                    </div>
                    <div class="col-span-2">
                        <div class="text-sm tracking-wide opacity-75 mb-0.5" :class="`text-${contentTextColorSecondary}`">
                            {{ translationSettings.portal_translation_raf_current_balance || 'Current balance' }}
                        </div>
                        <div class="text-sm font-semibold" :class="`text-${contentTextColorSecondary}`">
                            {{ stat.is_currency || stat.reward_type === 'shopify_store_credit' ? formatCurrencyAmount(stat.amount, stat.units) : `${formatNumber(stat.amount)} ${stat.units.toLowerCase()}` }}
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Payouts Section -->
        <section v-if="showPayouts && payoutsLoaded" class="space-y-4">
            <div class="flex items-center justify-between gap-4 flex-wrap">
                <div class="text-xl font-semibold" :class="`text-${contentTextColor}`">
                    {{ translationSettings.portal_translation_features_payouts || 'Payouts' }}
                </div>
                <div class="flex flex-wrap gap-3" v-if="!payoutRequestsDisabled">
                    <request-payout
                        v-for="option in payoutOptions"
                        :key="option.reward_amount_units"
                        :amount-unit="option.reward_amount_units || ''"
                        :amount-with-units-formatted="option.reward_amount_total_formatted"
                        :preferred-settings="getByKey(normalizedPreferredSettings, option.reward_amount_units)"
                        :available-methods="getByKey(normalizedPayoutGateways, option.reward_amount_units)"
                        :payout-invoice-requirements="payoutsSettings.payout_invoice_requirements"
                        :bill-to-details="payoutsSettings.bill_to_details"
                    />
                </div>
            </div>

            <div v-if="payoutHistoryRows.length > 0" :class="`${sectionContainerClasses} overflow-x-auto`">
                <table :class="`w-full text-sm text-left divide-y ${isCustomColor(programSettings.referral_portal_content_border_color) ? 'divide-content-custom-color' : `divide-${contentBorderColor}`}`">
                    <thead>
                        <tr>
                            <th scope="col" :class="`py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-${contentTextColorSecondary}`">
                                {{ translationSettings.portal_translation_general_date || 'Date' }}
                            </th>
                            <th scope="col" :class="`px-3 py-3.5 text-left text-sm font-semibold text-${contentTextColorSecondary}`">
                                {{ translationSettings.portal_translation_general_amount || 'Amount' }}
                            </th>
                            <th scope="col" :class="`px-3 py-3.5 text-left text-sm font-semibold text-${contentTextColorSecondary}`">
                                {{ translationSettings.portal_translation_general_status || 'Status' }}
                            </th>
                            <th scope="col" class="px-3 py-3.5"></th>
                        </tr>
                    </thead>
                    <tbody :class="`divide-y ${isCustomColor(programSettings.referral_portal_content_border_color) ? 'divide-content-custom-color' : `divide-${contentBorderColor}`}`">
                        <tr v-for="payout in payoutHistoryRows" :key="payout.id">
                            <td :class="`whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-${contentTextColorSecondary}`">
                                {{ payout.formatted_date }}
                            </td>
                            <td :class="`whitespace-nowrap px-3 py-4 text-sm font-semibold text-${contentTextColorSecondary}`">
                                {{ payout.formatted_amount }}
                            </td>
                            <td :class="`whitespace-nowrap px-3 py-4 text-sm text-${contentTextColorSecondary}`">
                                <span :class="`inline-flex items-center px-1.5 py-0.5 text-xs align-middle font-medium ${contentLayoutCorners === 'none' ? 'rounded-sm' : contentLayoutCorners} text-white bg-${isCustomColor(primaryColor) ? 'custom-primary' : primaryColor}`">
                                    {{ payoutStatusLabel(payout.status) }}
                                </span>
                            </td>
                            <td class="whitespace-nowrap px-3 py-4 text-sm text-right">
                                <btn
                                    v-if="canCancelPayout(payout)"
                                    type="light"
                                    size="xs"
                                    :loading="cancelPayoutLoadingId === payout.id"
                                    @click="cancelPayout(payout)"
                                >
                                    {{ translationSettings.portal_translation_general_cancel || 'Cancel' }}
                                </btn>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>

        <!-- Unlocked Rewards Section -->
        <section v-if="showUnlockedRewards && unlockedRewards.length > 0" class="space-y-4">
            <div class="space-y-2">
                <div class="text-xl font-semibold" :class="`text-${contentTextColor}`">
                    {{ translationSettings.portal_translation_raf_unlocked_rewards || 'Unlocked rewards' }}
                </div>
            </div>

            <div :class="sectionContainerClasses">
                <div
                    v-for="reward in unlockedRewards"
                    :key="reward.name"
                    class="grid grid-cols-4 lg:grid-cols-8 gap-4 p-4 flex items-center w-full"
                >
                    <div class="col-span-3 flex items-center gap-4">
                        <div :class="`flex flex-shrink-0 justify-center items-center w-8 h-8 font-semibold tracking-tight text-md bg-gray-50 ${contentLayoutCorners === 'none' ? 'rounded-sm' : contentLayoutCorners} text-${contentTextColorSecondary}`">
                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M14.5 2a3.5 3.5 0 0 1 3.163 5.001L21 7a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1l3.337.001a3.5 3.5 0 0 1 5.664-3.95A3.48 3.48 0 0 1 14.5 2zM18 13H6v7h12v-7zm2-4H4v2h16V9zM9.5 4a1.5 1.5 0 0 0-.144 2.993L9.5 7H11V5.5a1.5 1.5 0 0 0-1.356-1.493L9.5 4zm5 0l-.144.007a1.5 1.5 0 0 0-1.35 1.349L13 5.5V7h1.5l.144-.007a1.5 1.5 0 0 0 0-2.986L14.5 4z"></path>
                            </svg>
                        </div>
                        <div :class="`text-${contentTextColorSecondary}`">
                            <span class="flex text-md font-semibold">{{ reward.name }}</span>
                            <span class="flex text-sm">{{ reward.commission_type || '' }}</span>
                        </div>
                    </div>
                    <div class="col-span-2">
                        <span :class="`inline-flex items-center px-1.5 py-0.5 text-xs align-middle font-medium ${contentLayoutCorners === 'none' ? 'rounded-sm' : contentLayoutCorners} text-white bg-${isCustomColor(primaryColor) ? 'custom-primary' : primaryColor}`">
                            <span>
                                {{ reward.is_date_triggered ? (translationSettings.portal_translation_raf_for_a_special_date || 'For a special date') : (reward.is_referring ? (translationSettings.portal_translation_raf_for_a_referred_customer || 'For a referred customer') : (translationSettings.portal_translation_raf_as_a_referral || 'As a referral')) }}
                            </span>
                        </span>
                    </div>
                    <div class="col-span-1">
                        <div class="text-sm tracking-wide opacity-75 mb-0.5" :class="`text-${contentTextColorSecondary}`">
                            {{ translationSettings.portal_translation_raf_reward_triggered || 'Triggered' }}
                        </div>
                        <div class="text-sm font-semibold" :class="`text-${contentTextColorSecondary}`">
                            {{ formatNumber(reward.times_triggered) }}
                        </div>
                    </div>
                    <div class="col-span-2">
                        <div class="text-sm tracking-wide opacity-75 mb-0.5" :class="`text-${contentTextColorSecondary}`">
                            {{ translationSettings.portal_translation_raf_reward || 'Reward' }}
                        </div>
                        <div class="text-sm font-semibold" :class="`text-${contentTextColorSecondary}`">
                            {{ reward.show_reward_value ? `${reward.reward_value} ${reward.units || ''}` : (reward.is_currency ? formatCurrencyAmount(reward.amount, reward.units) : (reward.reward_type === 'shopify_store_credit' ? `${formatCurrencyAmount(reward.amount, reward.units)} store credit` : `${reward.amount} ${reward.units}`)) }}
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Rewards History Section -->
        <section v-if="showRewardsHistory && referralsActivities.length > 0" class="space-y-4">
            <div class="space-y-2">
                <div class="text-xl font-semibold" :class="`text-${contentTextColor}`">
                    {{ translationSettings.portal_translation_raf_history || 'History' }}
                </div>
            </div>

            <div :class="`${sectionContainerClasses} overflow-x-auto`">
                <table :class="`w-full text-sm text-left divide-y ${isCustomColor(programSettings.referral_portal_content_border_color) ? 'divide-content-custom-color' : `divide-${contentBorderColor}`}`">
                    <thead>
                        <tr>
                            <th scope="col" :class="`py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-${contentTextColorSecondary}`">
                                {{ translationSettings.portal_translation_general_date || 'Date' }}
                            </th>
                            <th scope="col" :class="`px-3 py-3.5 text-left text-sm font-semibold text-${contentTextColorSecondary}`">
                                {{ translationSettings.portal_translation_general_action || 'Action' }}
                            </th>
                        </tr>
                    </thead>
                    <tbody :class="`divide-y ${isCustomColor(programSettings.referral_portal_content_border_color) ? 'divide-content-custom-color' : `divide-${contentBorderColor}`}`">
                        <tr v-for="activity in referralsActivities" :key="activity.id">
                            <td :class="`whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-${contentTextColorSecondary}`">
                                {{ activity.parsed_date }}
                            </td>
                            <td :class="`whitespace-nowrap px-3 py-4 text-sm opacity-75 text-${contentTextColorSecondary}`">
                                {{ getActivityText(activity) }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>
        
        </div> <!-- End Dashboard Content -->
    </div> <!-- End Content Container -->

    <!-- Loading State -->
    <div v-else class="min-h-screen flex items-center justify-center">
        <div class="text-center">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto"></div>
            <p class="mt-2 text-sm text-gray-600">Loading dashboard...</p>
        </div>
    </div>
</template>

<script>
import {usePortalContentAndDesignStore} from "../../../../../../../stores/partners/portalContentAndDesignStore";

export default {
    methods: {
        getActivityText(activity) {
            const resourceType = activity.resource_type;
            const portalContentAndDesignStore = usePortalContentAndDesignStore();
            const translations = portalContentAndDesignStore.translationSettings || {};
            
            if (resourceType === 'customer') {
                return translations.portal_translation_raf_friend_signed_up || 'Friend signed up';
            } else if (resourceType === 'customer_reward') {
                return activity.is_credit 
                    ? (translations.portal_translation_raf_credit_reward || 'Credit reward')
                    : (translations.portal_translation_raf_you_got_rewarded || 'You got rewarded');
            } else if (resourceType === 'transaction') {
                return activity.is_credit 
                    ? (translations.portal_translation_raf_credit_purchase || 'Credit purchase')
                    : (translations.portal_translation_raf_friend_made_a_purchase || 'Friend made a purchase');
            }
            
            return '';
        }
    }
}
</script>